import React, { useEffect, useRef, useContext } from 'react'
import PropTypes from 'prop-types'
import {
  Wrapper,
  Terms,
  TermsLabel,
  Label,
  Text,
  Container,
  ScrollIndicator,
  ScrollText,
  ScrollIconWrapper
} from './style'
import { LanguageContext } from 'Rentlio/context/LanguageContext'
import { FormError } from 'Rentlio/components/UI/form'
import ArrowDown from 'Rentlio/components/Icons/arrowDown'

const TermsAndConditions = ({ termsOfService, termsScrolledThrough, termsRead, setHasScroll, locale }) => {
  const termsAndConditionsRef = useRef(null)
  const prevLocaleRef = useRef(locale)
  const { translate } = useContext(LanguageContext)

  useEffect(() => {
    if (locale !== prevLocaleRef.current && termsAndConditionsRef.current) {
      termsAndConditionsRef.current.scrollTop = 0
    }

    prevLocaleRef.current = locale
  }, [locale])

  useEffect(() => {
    const checkScrollEnd = () => {
      if (termsAndConditionsRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = termsAndConditionsRef.current

        // Add a small tolerance to account for floating-point precision issues
        const tolerance = 5

        if (scrollHeight - scrollTop - clientHeight <= tolerance) {
          termsScrolledThrough()
        }
      }
    }

    const checkIfScrollingIsRequired = () => {
      const { scrollHeight, clientHeight } = termsAndConditionsRef.current
      if (scrollHeight > clientHeight) {
        setHasScroll(true) // Call setHasScroll if scrolling is needed
      } else {
        termsScrolledThrough()
      }
    }

    const termsElement = termsAndConditionsRef.current
    if (termsElement) {
      termsElement.addEventListener('scroll', checkScrollEnd)
      checkIfScrollingIsRequired()
    }

    return () => {
      if (termsElement) {
        termsElement.removeEventListener('scroll', checkScrollEnd)
      }
    }
  }, [termsOfService])

  const scrollToBottom = () => {
    if (termsAndConditionsRef.current) {
      termsAndConditionsRef.current.scrollTo({
        top: termsAndConditionsRef.current.scrollHeight,
        behavior: 'smooth'
      })
    }
  }

  return (
    <Container>
      <TermsLabel>
        <Label>{translate('Terms and conditions')}</Label>
      </TermsLabel>
      {!termsRead && (
        <FormError>
          {translate(
            "Scroll through the Terms & Conditions below to unlock checkbox. Once accepted, you'll be able to enter your card details."
          )}
        </FormError>
      )}
      <Wrapper termsRead={termsRead}>
        <Terms ref={termsAndConditionsRef}>
          <Text>{termsOfService}</Text>
        </Terms>
        {!termsRead && (
          <ScrollIndicator onClick={scrollToBottom}>
            <ScrollText>{translate('Scroll to accept')}</ScrollText>
            <ScrollIconWrapper>
              <ArrowDown />
            </ScrollIconWrapper>
          </ScrollIndicator>
        )}
      </Wrapper>
    </Container>
  )
}

TermsAndConditions.propTypes = {
  termsOfService: PropTypes.string,
  termsScrolledThrough: PropTypes.func,
  termsRead: PropTypes.bool,
  setHasScroll: PropTypes.func,
  locale: PropTypes.string
}

export default TermsAndConditions
